import React, { useState, useEffect } from 'react';
import { Search, Filter, RotateCcw, Download } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const FilterToolbar = ({ filters, setFilters, onRefresh }) => {
  const [communes, setCommunes] = useState([]);
  const [agents, setAgents] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    fetchCommunes();
    fetchAgents();
  }, []);

  const fetchCommunes = async () => {
    try {
      const { data, error } = await supabase?.from('communes')?.select('id, name')?.order('name');
      
      if (error) throw error;
      setCommunes(data || []);
    } catch (error) {
      console.error('Error fetching communes:', error);
    }
  };

  const fetchAgents = async () => {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('id, full_name')?.eq('role', 'agent')?.order('full_name');
      
      if (error) throw error;
      setAgents(data || []);
    } catch (error) {
      console.error('Error fetching agents:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handlePriceRangeChange = (type, value) => {
    setFilters(prev => ({
      ...prev,
      priceRange: {
        ...prev?.priceRange,
        [type]: value
      }
    }));
  };

  const resetFilters = () => {
    setFilters({
      commune: '',
      propertyType: '',
      status: '',
      agent: '',
      priceRange: { min: '', max: '' }
    });
  };

  const exportData = () => {
    // Implement export functionality
    console.log('Exporting data...');
  };

  const propertyTypes = [
    { value: 'apartment', label: 'Appartement' },
    { value: 'house', label: 'Maison' },
    { value: 'villa', label: 'Villa' },
    { value: 'land', label: 'Terrain' },
    { value: 'commercial', label: 'Commercial' }
  ];

  const statusOptions = [
    { value: 'available', label: 'Disponible' },
    { value: 'reserved', label: 'Réservé' },
    { value: 'sold', label: 'Vendu' },
    { value: 'withdrawn', label: 'Retiré' }
  ];

  return (
    <div className="bg-card rounded-lg border border-border p-6 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <h3 className="text-lg font-semibold text-foreground">Filtres de Recherche</h3>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-2"
          >
            <Filter className="h-4 w-4" />
            {isExpanded ? 'Masquer' : 'Afficher'} les filtres
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={resetFilters}
            className="flex items-center gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            Réinitialiser
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            className="flex items-center gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            Actualiser
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={exportData}
            className="flex items-center gap-2"
          >
            <Download className="h-4 w-4" />
            Exporter
          </Button>
        </div>
      </div>
      {/* Basic Search */}
      <div className="flex items-center gap-4 mb-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher par nom de propriété ou adresse..."
            className="pl-10"
          />
        </div>
      </div>
      {/* Advanced Filters */}
      {isExpanded && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Commune
            </label>
            <Select
              value={filters?.commune}
              onValueChange={(value) => handleFilterChange('commune', value)}
              placeholder="Toutes les communes"
            >
              <option value="">Toutes les communes</option>
              {communes?.map((commune) => (
                <option key={commune?.id} value={commune?.name}>
                  {commune?.name}
                </option>
              ))}
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Type de Propriété
            </label>
            <Select
              value={filters?.propertyType}
              onValueChange={(value) => handleFilterChange('propertyType', value)}
              placeholder="Tous les types"
            >
              <option value="">Tous les types</option>
              {propertyTypes?.map((type) => (
                <option key={type?.value} value={type?.value}>
                  {type?.label}
                </option>
              ))}
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Statut
            </label>
            <Select
              value={filters?.status}
              onValueChange={(value) => handleFilterChange('status', value)}
              placeholder="Tous les statuts"
            >
              <option value="">Tous les statuts</option>
              {statusOptions?.map((status) => (
                <option key={status?.value} value={status?.value}>
                  {status?.label}
                </option>
              ))}
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Agent
            </label>
            <Select
              value={filters?.agent}
              onValueChange={(value) => handleFilterChange('agent', value)}
              placeholder="Tous les agents"
            >
              <option value="">Tous les agents</option>
              {agents?.map((agent) => (
                <option key={agent?.id} value={agent?.id}>
                  {agent?.full_name}
                </option>
              ))}
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Prix Min (XOF)
            </label>
            <Input
              type="number"
              placeholder="0"
              value={filters?.priceRange?.min}
              onChange={(e) => handlePriceRangeChange('min', e?.target?.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Prix Max (XOF)
            </label>
            <Input
              type="number"
              placeholder="∞"
              value={filters?.priceRange?.max}
              onChange={(e) => handlePriceRangeChange('max', e?.target?.value)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default FilterToolbar;